import type { WsEvent } from '@hotel-manager/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import type { UseWsConnection } from './useWsEvents.js';
import { useWsEvents } from './useWsEvents.js';

/**
 * HM-072 — Event handlers WS + query invalidation + toast minimal.
 */
export type LiveToast = {
  id: string;
  text: string;
  level: 'info' | 'success' | 'warning' | 'error';
};

export type LiveConflict = {
  bookingId: string;
  reason: string;
  roomId?: string | null;
};

export type LiveHalAlert = {
  driver: string;
  kind: string;
  severity: 'info' | 'error';
};

export type DashboardLiveSync = {
  toasts: LiveToast[];
  conflicts: LiveConflict[];
  halAlerts: LiveHalAlert[];
  /**
   * HM-473 audit A7: roomId che stanno lampeggiando perche' il cliente
   * ha usato un codice alla sbarra uscita (Spec §7.4). Auto-clear dopo
   * 10 secondi se l'operatore non agisce.
   */
  blinkingExitRoomIds: Set<string>;
  dismissToast: (id: string) => void;
};

export function useDashboardLiveSync(ws: UseWsConnection): DashboardLiveSync {
  const queryClient = useQueryClient();
  const [toasts, setToasts] = useState<LiveToast[]>([]);
  const [conflicts, setConflicts] = useState<LiveConflict[]>([]);
  const [halAlerts, setHalAlerts] = useState<LiveHalAlert[]>([]);
  const [blinkingExitRoomIds, setBlinkingExitRoomIds] = useState<Set<string>>(new Set());

  const pushToast = useCallback((t: Omit<LiveToast, 'id'>) => {
    const id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setToasts((cur) => [...cur, { ...t, id }]);
    setTimeout(() => {
      setToasts((cur) => cur.filter((x) => x.id !== id));
    }, 5_000);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const handler = useCallback(
    (ev: WsEvent) => {
      // HM-105: invalidate kitchen-orders su qualunque evento room_service.*
      if (typeof ev.event === 'string' && ev.event.startsWith('room_service.')) {
        queryClient.invalidateQueries({ queryKey: ['kitchen-orders'] });
        return;
      }
      switch (ev.event) {
        case 'room.state_changed':
          queryClient.invalidateQueries({ queryKey: ['rooms'] });
          break;
        case 'booking.created':
          queryClient.invalidateQueries({ queryKey: ['bookings'] });
          queryClient.invalidateQueries({ queryKey: ['rooms'] });
          // Fix stats planning stantie: PlanningPage usa la queryKey
          // ['planning', 'stats', <ymd>] — l'invalidazione per prefisso
          // copre tutte le date. Senza, navigando via SPA le stats
          // restavano vecchie fino a un reload.
          queryClient.invalidateQueries({ queryKey: ['planning', 'stats'] });
          pushToast({ text: `Booking creato: ${ev.payload.guestName}`, level: 'success' });
          break;
        case 'booking.updated':
          queryClient.invalidateQueries({ queryKey: ['bookings'] });
          queryClient.invalidateQueries({ queryKey: ['rooms'] });
          queryClient.invalidateQueries({ queryKey: ['planning', 'stats'] });
          break;
        case 'booking.no_show':
          queryClient.invalidateQueries({ queryKey: ['bookings'] });
          queryClient.invalidateQueries({ queryKey: ['rooms'] });
          pushToast({ text: `No-show: ${ev.payload.guestName}`, level: 'warning' });
          break;
        case 'booking.conflict_detected':
          queryClient.invalidateQueries({ queryKey: ['conflicts'] });
          setConflicts((cur) => {
            if (cur.some((c) => c.bookingId === ev.payload.bookingId)) return cur;
            return [
              ...cur,
              { bookingId: ev.payload.bookingId, reason: ev.payload.reason, roomId: null },
            ];
          });
          pushToast({ text: `Conflict: ${ev.payload.reason}`, level: 'error' });
          break;
        case 'hal.event': {
          const isError = ev.payload.kind === 'bus.down' || ev.payload.kind === 'printer.error';
          if (isError) {
            setHalAlerts((cur) => {
              const exists = cur.some(
                (a) => a.driver === ev.payload.driver && a.kind === ev.payload.kind,
              );
              if (exists) return cur;
              return [
                ...cur,
                { driver: ev.payload.driver, kind: ev.payload.kind, severity: 'error' },
              ];
            });
          } else if (ev.payload.kind === 'bus.up') {
            setHalAlerts((cur) => cur.filter((a) => !(a.driver === ev.payload.driver)));
          } else if (ev.payload.kind === 'gate_opened_exit') {
            // HM-473 audit A7: Spec §7.4 — codice usato alla sbarra uscita.
            // Lampeggia la tile camera per 10s; l'operatore poi conferma
            // check-out o "ospite ancora in camera".
            const roomId = (ev.payload.data as { roomId?: string }).roomId;
            if (roomId) {
              setBlinkingExitRoomIds((cur) => {
                if (cur.has(roomId)) return cur;
                const next = new Set(cur);
                next.add(roomId);
                return next;
              });
              pushToast({
                text: `Codice uscita usato — camera ${roomId.slice(0, 8)}`,
                level: 'warning',
              });
              setTimeout(() => {
                setBlinkingExitRoomIds((cur) => {
                  if (!cur.has(roomId)) return cur;
                  const next = new Set(cur);
                  next.delete(roomId);
                  return next;
                });
              }, 10_000);
            }
          }
          break;
        }
        default:
          break;
      }
    },
    [pushToast, queryClient],
  );

  useWsEvents(ws.client, handler);

  // cleanup conflicts/toasts on unmount: react auto via state
  useEffect(() => {
    return () => {
      setToasts([]);
    };
  }, []);

  return { toasts, conflicts, halAlerts, blinkingExitRoomIds, dismissToast };
}
